import React from "react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import { Loader2 } from "lucide-react";

/**
 * Loading spinner component with customizable size
 */
export const Spinner = ({ className, size = 16 }) => (
    <Loader2 className={cn("animate-spin text-muted-foreground", className)} size={size} />
);

/**
 * Info card component for displaying metadata using shadcn Card
 */
export const InfoCard = ({ label, children, className }) => (
    <Card className={cn("min-w-[140px] py-0 shadow-none", className)}>
        <CardContent className="flex flex-col items-center gap-2 px-4 py-3">
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                {label}
            </span>
            <div className="text-sm font-medium">{children}</div>
        </CardContent>
    </Card>
);

/**
 * Action button with tooltip support using shadcn Button and Tooltip
 */
export const ActionButton = ({ tooltip, onClick, active, activeColor, children, className, disabled }) => (
    <Tooltip>
        <TooltipTrigger asChild>
            <Button
                variant="ghost"
                size="icon"
                onClick={onClick}
                disabled={disabled}
                className={cn(
                    "h-9 w-9 transition-all duration-200 hover:scale-105",
                    active && activeColor,
                    className
                )}
            >
                {children}
            </Button>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="font-medium">
            {tooltip}
        </TooltipContent>
    </Tooltip>
);

/**
 * Loading state component for the modal using shadcn Skeleton
 */
export const LoadingState = () => (
    <div className="space-y-6 animate-in fade-in duration-300">
        {/* Title skeleton */}
        <div className="space-y-3">
            <Skeleton className="h-8 w-3/4" />
            <Skeleton className="h-4 w-1/2" />
        </div>
        
        {/* Tags skeleton */}
        <div className="flex gap-2">
            <Skeleton className="h-6 w-16 rounded-full" />
            <Skeleton className="h-6 w-20 rounded-full" />
            <Skeleton className="h-6 w-14 rounded-full" />
        </div>
        
        {/* Metadata cards skeleton */}
        <div className="flex gap-3">
            <Skeleton className="h-20 w-36 rounded-lg" />
            <Skeleton className="h-20 w-36 rounded-lg" />
            <Skeleton className="h-20 w-36 rounded-lg" />
        </div>
        
        {/* Description skeleton */}
        <div className="space-y-2">
            <Skeleton className="h-5 w-24" />
            <Skeleton className="h-32 w-full rounded-lg" />
        </div>
        
        {/* Comments skeleton */}
        <div className="space-y-3">
            <Skeleton className="h-5 w-20" />
            <div className="space-y-2">
                <Skeleton className="h-16 w-full rounded-lg" />
                <Skeleton className="h-16 w-full rounded-lg" />
            </div>
        </div>
    </div>
);

/**
 * Helper function to format time ago
 */
export const formatTimeAgo = (timestamp) => {
    if (!timestamp) return "";
    const diff = Date.now() - new Date(timestamp);
    const minutes = Math.floor(diff / 60000);
    if (minutes < 1) return "just now";
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    return `${Math.floor(hours / 24)}d ago`;
};

/**
 * Helper function to format file size
 */
export const formatFileSize = (bytes) => {
    if (!bytes) return "";
    const kb = bytes / 1024;
    const mb = kb / 1024;
    return mb > 1 ? `${mb.toFixed(1)} MB` : `${kb.toFixed(0)} KB`;
};

/**
 * Get star level properties for display
 */
export const getStarProps = (level) => {
    const colors = ["", "text-green-500", "text-orange-500", "text-red-500"];
    const tooltips = [
        "Not starred (click to star) • Ctrl+Q",
        "Level 1 (click for Level 2) • Ctrl+Q",
        "Level 2 (click for Level 3) • Ctrl+Q",
        "Level 3 (click to unstar) • Ctrl+Q",
    ];
    return {
        filled: level > 0,
        color: colors[level],
        tooltip: tooltips[level],
    };
};
